import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import QRCodeDecor from "../../../components/base/QRCodeDecor";

interface TeamMember {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  jobRole: string;
  reputation: number;
  joinedAt: string;
  isMe: boolean;
  employeeNumber: string;
}

interface TaskCompletion {
  id: string;
  task_id: string;
  hub: string;
  completed_at: string;
}

interface FullState {
  capital: number;
  reputation: number;
  creditScore: number;
}

interface Props {
  member: TeamMember | null;
  workspaceId: string | null;
  onClose: () => void;
}

const JOB_ROLE_LABELS: Record<string, string> = {
  // MusicLabel
  CEO: "대표이사", AR: "A&R 담당", Finance: "재무 담당",
  Marketing: "마케팅 담당", Legal: "법무 담당",
  // LiveEntertainment
  PD: "공연기획 PD", Ticketing: "티켓영업 MD",
  // CulturalFoundation
  Projects: "사업팀장", Fundraising: "후원개발팀", Evaluation: "사업평가위원",
  // WebtoonStudio
  Editor: "에디터", Contract: "계약 MD",
  // ContentAgency
  Creative: "크리에이티브 디렉터", AE: "영업 AE",
  Production: "영상 PD", Data: "데이터 분석가",
};

const HUB_META: Record<string, { label: string; icon: string; color: string }> = {
  foundation: { label: "창업 기초", icon: "ri-building-line", color: "#7A9BFF" },
  ar: { label: "A&R", icon: "ri-music-2-line", color: "#FF9F43" },
  campaign: { label: "캠페인", icon: "ri-megaphone-line", color: "#E8375F" },
  concert: { label: "공연기획", icon: "ri-live-line", color: "#00C896" },
  ip: { label: "IP", icon: "ri-copyright-line", color: "#A78BFA" },
};

function getHubMeta(hub: string) {
  const k = hub?.toLowerCase().replace(/-hub$/, "").replace(/hub$/, "").trim();
  return HUB_META[k] ?? { label: hub ?? "기타", icon: "ri-task-line", color: "#5A5E72" };
}

function getRankInfo(rep: number) {
  if (rep >= 81) return { label: "팀장", color: "#EAB308" };
  if (rep >= 61) return { label: "과장", color: "#E8375F" };
  if (rep >= 41) return { label: "대리", color: "#FF9F43" };
  if (rep >= 21) return { label: "사원", color: "#7A9BFF" };
  return { label: "인턴", color: "#8B91A8" };
}

function MiniBarcode() {
  const p = [1,2,1,3,1,2,2,1,3,1,2,1,1,3,2,1,2,1];
  return (
    <div className="flex items-end gap-[1px]" style={{ height: 16 }}>
      {p.map((w, i) => (
        <div key={i} style={{ width: w, height: i % 2 === 0 ? 16 : 10, backgroundColor: "rgba(255,255,255,0.2)" }} />
      ))}
    </div>
  );
}

export default function TeamMemberModal({ member, workspaceId, onClose }: Props) {
  const navigate = useNavigate();
  const [completions, setCompletions] = useState<TaskCompletion[]>([]);
  const [state, setState] = useState<FullState | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!member || !workspaceId) return;
    setLoading(true);

    const [{ data: tc }, { data: cs }] = await Promise.all([
      supabase.from("task_completions")
        .select("id, task_id, hub, completed_at")
        .eq("workspace_id", workspaceId)
        .eq("user_id", member.userId)
        .order("completed_at", { ascending: false }),
      supabase.from("company_states")
        .select("capital, reputation, credit_score")
        .eq("workspace_id", workspaceId)
        .eq("user_id", member.userId)
        .maybeSingle(),
    ]);

    setCompletions(tc ?? []);
    if (cs) setState({ capital: cs.capital, reputation: cs.reputation, creditScore: cs.credit_score });
    setLoading(false);
  }, [member, workspaceId]);

  useEffect(() => { fetch(); }, [fetch]);

  if (!member) return null;

  const rank = getRankInfo(state?.reputation ?? member.reputation);
  const rep = state?.reputation ?? member.reputation;
  const jobLabel = JOB_ROLE_LABELS[member.jobRole] ?? member.jobRole ?? "—";
  const joinDate = new Date(member.joinedAt).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });

  // 허브별 그룹
  const grouped = completions.reduce<Record<string, TaskCompletion[]>>((acc, c) => {
    const k = c.hub ?? "unknown";
    if (!acc[k]) acc[k] = [];
    acc[k].push(c);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl bg-[#0D0E14] border border-white/[0.08] rounded-2xl overflow-hidden"
        style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="px-5 py-3.5 border-b border-white/[0.06] flex items-center justify-between shrink-0">
          <span className="text-[#888888] text-xs font-semibold uppercase tracking-widest">팀원 상세</span>
          <button type="button" onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-[#555555] hover:text-white hover:bg-white/[0.06] transition-colors cursor-pointer">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col md:flex-row gap-0">
            {/* 왼쪽: 사원증 카드 */}
            <div className="md:w-56 shrink-0 p-5 flex justify-center"
              style={{ background: "linear-gradient(160deg, #0f1020 0%, #111830 100%)", borderRight: "1px solid rgba(255,255,255,0.05)" }}>
              <div
                className="w-44 rounded-xl overflow-hidden"
                style={{
                  background: member.isMe
                    ? "linear-gradient(160deg, #1a1a2e 0%, #1C2540 60%, #0f3460 100%)"
                    : "linear-gradient(160deg, #141420 0%, #18192a 100%)",
                  border: member.isMe ? "1px solid rgba(75,108,247,0.4)" : "1px solid rgba(255,255,255,0.08)",
                  boxShadow: member.isMe ? "0 0 30px rgba(75,108,247,0.15)" : "none",
                }}
              >
                {/* 상단 컬러 라인 */}
                <div className="h-0.5" style={{ background: `linear-gradient(90deg, transparent, ${rank.color}70, transparent)` }} />

                {/* 회사 헤더 */}
                <div className="px-3 pt-3 pb-2 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center rounded bg-[#4B6CF7]/20 border border-[#4B6CF7]/25">
                      <i className="ri-music-2-line text-[#7A9BFF] text-[9px]"></i>
                    </div>
                    <div className="text-white text-[8px] font-bold tracking-widest">K-ArtPEN</div>
                  </div>
                  <div className="text-white/15 text-[7px] uppercase tracking-wider">사원증</div>
                </div>

                <div className="mx-3 h-px bg-white/[0.05]" />

                {/* 증명사진 */}
                <div className="flex justify-center pt-3 pb-2">
                  <div className="w-16 h-20 rounded-lg overflow-hidden border border-white/10"
                    style={{ background: "linear-gradient(135deg, #252830, #1C1F2E)" }}>
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} alt={member.displayName} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-user-3-line text-white/15 text-2xl"></i>
                      </div>
                    )}
                  </div>
                </div>

                {/* 정보 */}
                <div className="px-3 pb-2 text-center">
                  <div className="text-white text-[11px] font-bold mb-0.5 truncate">{member.displayName}</div>
                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ color: rank.color, backgroundColor: `${rank.color}18`, border: `1px solid ${rank.color}35` }}>
                    {rank.label}
                  </span>
                  <div className="text-white/30 text-[8px] mt-1 mb-2 truncate">{jobLabel}</div>
                  <div className="w-full h-0.5 bg-white/[0.06] rounded-full overflow-hidden mb-2">
                    <div className="h-full rounded-full" style={{ width: `${rep}%`, backgroundColor: rank.color, opacity: 0.5 }} />
                  </div>
                  <div className="text-white/20 text-[7px] space-y-0.5">
                    <div className="flex items-center gap-1 justify-center">
                      <i className="ri-shield-keyhole-line text-[7px]"></i>
                      <span className="font-mono">{member.employeeNumber}</span>
                    </div>
                    <div className="flex items-center gap-1 justify-center">
                      <i className="ri-calendar-line text-[7px]"></i>
                      <span>{joinDate}</span>
                    </div>
                  </div>
                </div>

                {/* QR + 바코드 영역 */}
                <div className="px-3 pb-3">
                  <div className="flex items-end justify-between gap-2">
                    <QRCodeDecor
                      seed={member.userId}
                      cellSize={3}
                      opacity={0.25}
                      link={`${window.location.origin}/profile?uid=${member.userId}`}
                      tooltip={`${member.displayName}의 프로필 링크 — 클릭하여 열기`}
                    />
                    <div className="flex flex-col items-center gap-0.5">
                      <MiniBarcode />
                      <div className="text-white/10 text-[6px] font-mono">{member.employeeNumber.slice(-4)}</div>
                    </div>
                  </div>
                </div>

                <div className="h-0.5" style={{ background: "linear-gradient(90deg, transparent, rgba(75,108,247,0.4), transparent)" }} />
              </div>
            </div>

            {/* 오른쪽: 업무 현황 */}
            <div className="flex-1 p-5 space-y-4">
              {/* 스탯 카드 */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "직급", value: rank.label, sub: `${rep}pt`, color: rank.color },
                  { label: "자본금", value: state ? `${(state.capital).toLocaleString()}만` : "—", sub: "원", color: "#00C896" },
                  { label: "완료 태스크", value: `${completions.length}건`, sub: `${Object.keys(grouped).length}개 허브`, color: "#7A9BFF" },
                ].map((s, i) => (
                  <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-3 py-2.5 text-center">
                    <div className="text-[#3E4155] text-[9px] uppercase tracking-wider mb-1">{s.label}</div>
                    <div className="font-bold text-sm" style={{ color: s.color }}>{s.value}</div>
                    <div className="text-[#3E4155] text-[9px]">{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* 직급 프로그레스 */}
              <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl px-4 py-3">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-[#3E4155] text-[10px] uppercase tracking-wider">직급 경력 점수</span>
                  <span className="text-[#555555] text-[10px]">{rep} / 100</span>
                </div>
                <div className="w-full h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${rep}%`, backgroundColor: rank.color, opacity: 0.6 }} />
                </div>
                <div className="flex justify-between mt-1">
                  {["인턴", "사원", "대리", "과장", "팀장"].map((r, i) => (
                    <span key={r} className="text-[8px]"
                      style={{ color: rep >= [0,21,41,61,81][i] ? rank.color : "#3E4155", opacity: rep >= [0,21,41,61,81][i] ? 0.7 : 0.3 }}>
                      {r}
                    </span>
                  ))}
                </div>
              </div>

              {/* 태스크 완료 내역 */}
              <div>
                <div className="text-[#3E4155] text-[10px] uppercase tracking-widest mb-2">태스크 완료 내역</div>
                {loading ? (
                  <div className="text-center py-6 text-[#3E4155] text-xs animate-pulse">불러오는 중...</div>
                ) : completions.length === 0 ? (
                  <div className="text-center py-6">
                    <i className="ri-inbox-line text-2xl text-[#252830] block mb-2"></i>
                    <p className="text-[#3E4155] text-xs">완료한 태스크가 없어요</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {Object.entries(grouped).map(([hub, items]) => {
                      const meta = getHubMeta(hub);
                      return (
                        <div key={hub} className="bg-white/[0.02] border border-white/[0.05] rounded-xl overflow-hidden">
                          <div className="px-3 py-2 flex items-center justify-between border-b border-white/[0.04]">
                            <div className="flex items-center gap-1.5">
                              <i className={`${meta.icon} text-xs`} style={{ color: meta.color }}></i>
                              <span className="text-[#AAAAAA] text-[10px] font-medium">{meta.label}</span>
                            </div>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                              style={{ color: meta.color, backgroundColor: `${meta.color}15` }}>
                              {items.length}건
                            </span>
                          </div>
                          <div className="divide-y divide-white/[0.03]">
                            {items.slice(0, 5).map(item => (
                              <div key={item.id} className="px-3 py-1.5 flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <i className="ri-checkbox-circle-fill text-emerald-400/50 text-xs"></i>
                                  <span className="text-[#666666] text-[10px] font-mono">{item.task_id}</span>
                                </div>
                                <span className="text-[#3E4155] text-[9px]">
                                  {new Date(item.completed_at).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" })}
                                </span>
                              </div>
                            ))}
                            {items.length > 5 && (
                              <div className="px-3 py-1.5 text-center text-[#3E4155] text-[9px]">
                                +{items.length - 5}건 더 있음
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 내 카드면 프로필 편집 버튼 */}
              {member.isMe && (
                <button type="button" onClick={() => { onClose(); navigate("/profile"); }}
                  className="w-full py-2.5 rounded-xl border border-[#4B6CF7]/30 text-[#7A9BFF] text-xs font-medium hover:bg-[#4B6CF7]/10 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-edit-2-line text-sm"></i>
                  내 프로필 편집하기
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
